/**
 * Property Integrations tab – Invitation API + Activate API + Utility Concierge (Angular).
 * Same main form as Property Info; header Save persists activate_api / disable_uc.
 * Generate token still POSTs immediately (Angular).
 */
import { useState, useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { KeyRound, Loader2 } from 'lucide-react'
import { useUserRole } from '@/hooks/useUserRole'
import apiClient from '@/lib/api/apiClient'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { SectionCard } from '@/components/ui/SectionCard'
import companyFormStyles from '@/features/company/styles/companyAdd.module.css'
import tabStyles from '@/features/company/styles/companyInfoTab.module.css'
import { COMM_FIELD_BASE, COMM_LABEL } from '@/lib/styles/companyEditTabShell'
import { cn } from '@/lib/utils/helpers'
import type { PropertyIntegrationsTabProps } from '../types'

const FIELD_CLASS = cn(COMM_FIELD_BASE, 'h-10 w-full bg-white text-slate-700')
const FIELD_READONLY = cn(COMM_FIELD_BASE, 'h-10 w-full cursor-not-allowed bg-slate-50 text-slate-700')

export function PropertyIntegrationsTab({
  control,
  property,
  propertyId,
  onRefresh,
}: PropertyIntegrationsTabProps) {
  const { hasPolicy } = useUserRole()
  const canEditApiKey = hasPolicy(['property_edit_api_key'])
  const canGenerateKey = hasPolicy(['property_generate_api_key'])

  const [invitationApiKey, setInvitationApiKey] = useState<string>(
    String(property?.invitation_api_key ?? property?.invitationApiKey ?? ''),
  )
  const [isGenerating, setIsGenerating] = useState(false)

  useEffect(() => {
    setInvitationApiKey(String(property?.invitation_api_key ?? property?.invitationApiKey ?? ''))
  }, [property?.invitation_api_key, property?.invitationApiKey])

  const handleGenerateKey = async () => {
    setIsGenerating(true)
    try {
      const res = await apiClient.post(`/api/property/${propertyId}`, { generate_key: 1 })
      setInvitationApiKey(
        typeof res.data === 'string' ? res.data : res.data?.invitation_api_key ?? invitationApiKey,
      )
      showSuccess('New Token Generated')
      onRefresh?.()
    } catch (err) {
      showApiError(err)
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div
      data-testid="property-integrations-tab"
      className={`${tabStyles.tabContent} animate-in fade-in duration-300`}
    >
      <SectionCard
        icon={KeyRound}
        title="Invitation API"
        testId="property-integrations-invitation-api-section"
        titleTestId="property-integrations-invitation-api-title"
      >
        <div
          className={cn(
            'grid grid-cols-1 items-end gap-6 md:grid-cols-[minmax(0,14rem)_minmax(0,20rem)_auto]',
            !canEditApiKey && 'pointer-events-none opacity-75',
          )}
        >
          <div className="w-full min-w-0">
            <label
              className={COMM_LABEL}
              htmlFor="property-integrations-activate-api-select"
              data-testid="property-integrations-activate-api-label"
            >
              Activate API
            </label>
            <Controller
              name="activate_api"
              control={control}
              render={({ field }) => (
                <select
                  id="property-integrations-activate-api-select"
                  data-testid="property-integrations-activate-api-select"
                  className={FIELD_CLASS}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                >
                  <option value="">Select Type</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              )}
            />
          </div>

          <div className="w-full min-w-0">
            <label
              className={COMM_LABEL}
              data-testid="property-integrations-api-token-label"
              htmlFor="property-integrations-api-token"
            >
              Invitation API Token
            </label>
            <input
              id="property-integrations-api-token"
              type="text"
              readOnly
              value={invitationApiKey}
              placeholder="API Token"
              data-testid="property-integrations-api-token-input"
              className={FIELD_READONLY}
            />
          </div>

          {canGenerateKey ? (
            <div className="w-full md:w-auto">
              <button
                type="button"
                onClick={handleGenerateKey}
                disabled={isGenerating || !canEditApiKey}
                data-testid="property-integrations-generate-token-button"
                className={cn(companyFormStyles.btnPrimary, 'min-w-[160px] gap-2')}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Generating...
                  </>
                ) : (
                  'Generate New Token'
                )}
              </button>
            </div>
          ) : null}
        </div>
      </SectionCard>
    </div>
  )
}
